import '../broiler/poultryDesign.css';
import './layerBatchesRedesign.css';
import Modal, {ModalFooter} from '../shared/Modal.js';
import Button from '../shared/Button.js';
// LayerBatchesView — hub + router for layer batches and housings.
//
// Hub shows a unified vertical inspection grid (header row + aligned nav-only
// rows) of layer batches with summary stats, plus a right-sized toolbar:
// search, status, supplier, start-date range, bird-count range, a single
// active sort rule, saved views (surface_key 'layer.batches'), and CSV/print
// export of the filtered+sorted set. Filter/sort semantics live in the pure
// lib src/lib/layerBatchFilters.js. Row clicks navigate to /layer/batches/<id>
// with the SORTED set as the record-sequence order. The per-record workspace
// (metadata edit, lifecycle phases, housings list, batch delete cascade) lives
// on LayerBatchPage; layer.housing records have their own page at
// /layer/housings/<id>. Both record pages own Comments + collapsed Activity
// via RecordCollaborationSection.
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import {getProgramColor} from '../lib/programColors.js';
import {layerBatchIdFromName, layerBatchIdTaken, nextLayerBatchName} from '../lib/layerBatchName.js';
import {getReadableText} from '../lib/styles.js';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';
import InlineNotice from '../shared/InlineNotice.jsx';

import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';

import Badge from '../shared/Badge.jsx';
import {GroupDot} from '../shared/GroupPillPicker.js';
import {buildLayerGroupPalette, layerGroupColor} from './layerGroupPalette.js';
import {layerGroupStatusLabel, resolveLayerGroupStatus} from '../lib/layerGroupStatus.js';
import type {LayerGroupsPayload} from '../lib/layerGroupStatus.js';

import StatusText from '../shared/StatusText.jsx';
import {computeHousingDisplayCount, computeLayerFeedCost} from '../lib/layerHousing.js';
import {applyLayerOperation, layerRejectionMessage} from '../lib/layerOperationsApi.js';
import {createRequestIdMemo} from './layerRecordOps.js';
import {buildLayerBatchComparator} from '../lib/layerBatchFilters.js';
import {computeBatchStats} from './layerBatchStats.js';
import type {LayerStatTotals} from './layerBatchStats.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {LayerBatchesCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import LayerBatchPage from './LayerBatchPage.tsx';
import LayerHousingPage from './LayerHousingPage.tsx';
import {sb} from '../lib/supabase.js';
import {fmtDate as fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import Header from '../shared/Header.tsx';
import type {Notice} from '../shared/InlineNotice.js';

const LAYER_BATCHES_PARAMS = Object.freeze({});

// NO PROPS LEFT AT ALL. This router now takes nothing from App.
//
// The five data props LayerEntry used to thread here — layerGroups,
// layerBatches, layerHousings, batches and feedCosts — were App's former-eager
// boot results. The record surfaces read the first four for themselves now,
// through layer.record.core and layer.record.appStore, which is what lets
// `layerbatches` retire from all three frozen consumer lists in
// tests/fixtures/former-eager-dependencies.json. `feedCosts` did not move: it
// was DEAD. LayerBatchPage declared and destructured it and never read it.
//
// The two SETTERS — LayerContext's raw `setLayerBatches` and App's
// `persistLayerHousings` — outlived those by one wave, because a write is not a
// read. They left with the `layer.batches.detail` boot release, which was the
// last branch under this router still holding them: `layer.housings.detail` had
// already dropped `setLayerHousings` and `layer.batches.index` never took
// either. Neither setter was ever a database write — all of LayerBatchPage's
// three writes are page-local and unmoved — and both are redundant on a
// released surface. See that surface's declaration for why removing them had to
// land with the release itself.
//
// Both record branches are now released, so both have a mount at the foot of
// this file. The router itself survives for the shapes the two release fences
// refuse, which still take the legacy boot and still land here.

// A layer_batches row, reduced to the four columns this hub reads. `id` is
// required and non-null for the same reason layerBatchStats.ts declares it that
// way — it is the table's primary key and computeBatchStats keys its result by
// it. `name` is required because the grid renders `row.name` with no guard;
// saveNewBatch, the only creator of these rows, refuses an empty trimmed name.
type LayerBatchRecord = {
  id: string;
  name: string;
  status?: string | null;
  supplier?: string | null;
  per_lb_starter_cost?: number | string | null;
  per_lb_grower_cost?: number | string | null;
  per_lb_layer_cost?: number | string | null;
};

// A layer_housings row — the three columns read here plus the `id`
// computeBatchStats requires (layerBatchStats.ts declares the same uuid).
type LayerHousingRecord = {
  id: string;
  batch_id?: string | null;
  housing_name?: string | null;
  status?: string | null;
};

// The `e.message` half of the `e?.message || e` fallbacks below. A caught value
// is `unknown` under strict mode, and `instanceof Error` is the wrong probe:
// supabase-js rejects with plain PostgrestError objects that carry `message`
// without being Errors. Returning '' for a value with no usable message leaves
// the existing `||` fallbacks behaving exactly as before.
function errMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const message = (e as {message?: unknown}).message;
    if (message) return String(message);
  }
  return '';
}

// Shared column template for the unified Layer Batches inspection grid. The hub
// header row and every batch row consume the SAME template so the columns stay
// aligned, Podio-style. Tracks:
//   Batch | Status | Housings | Hens | Feed | Mort. | Dozens | Cost | Open
const LAYER_BATCH_GRID_COLUMNS = 'minmax(120px, 1.4fr) 84px minmax(120px, 1.6fr) 70px 92px 64px 72px 84px 56px';

// The exact index hub is independent of LayerEntry/App props — and since the
// two record releases, so is every other branch of LayerBatchesRouter below.
// Do NOT merge those branches back together anyway: the hub is reached by an
// EQUALITY on `/layer/batches` while each record release is a prefix contract
// with its own fence, and a merged branch would hand a fenced shape a released
// renderer or the reverse.
export const LayerBatchesHub = () => {
  const {authState} = useAuth();
  const {pendingEdit, setPendingEdit} = useUI();
  const navigate = useNavigate();
  const location = useLocation();
  const {useState, useEffect} = React;
  const layerResource = useRouteResource(RESOURCE.LAYER_BATCHES_CORE, LAYER_BATCHES_PARAMS);
  const layerData = layerResource.data as LayerBatchesCoreData | null;
  // The one place this hub states what it believes the two opaque route-core
  // arrays hold. Asserting once here keeps every field read below unannotated.
  const layerBatches = (layerData?.layerBatches || []) as LayerBatchRecord[];
  // The canonical groups this surface already declares (layer.eggDailys.groups,
  // a permanent obligation): the Retirement Home status AND the R06 group
  // palette both read it, so it loads for normal groups too.
  const groupResource = useRouteResource(RESOURCE.EGG_DAILYS_GROUPS, LAYER_BATCHES_PARAMS);
  const retirementHomeStatus = resolveLayerGroupStatus({
    data: groupResource.data as LayerGroupsPayload | null,
    error: groupResource.error,
  }).index.get('retirement home');
  // R06: group dots by canonical order, from that same canonical read. While it
  // is pending, failed or denied, names carry no dot rather than a guessed one.
  const groupPalette = buildLayerGroupPalette((groupResource.data as LayerGroupsPayload | null)?.layerGroups);
  const layerHousings = (layerData?.layerHousings || []) as LayerHousingRecord[];
  const rawLayerDailys = layerData?.layerDailys || [];
  const rawEggDailys = layerData?.eggDailys || [];
  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';
  const loading = layerResource.loading;
  const loadError: Notice | null = layerResource.error
    ? {
        kind: 'error',
        message: 'Could not load layer batches: ' + (errMessage(layerResource.error) || String(layerResource.error)),
      }
    : null;
  const loadLayerMetrics = layerResource.refetch;
  const [notice, setNotice] = useState<Notice | null>(null);

  const [showAddBatch, setShowAddBatch] = useState(false);
  const [addName, setAddName] = useState('');
  const [addArrivalDate, setAddArrivalDate] = useState('');
  const [addStatus, setAddStatus] = useState('active');
  const [addErr, setAddErr] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  // One request id per create intent (REVIEW-242 F3): a retry after an unknown
  // reply replays the committed create instead of reading as "already in use".
  const [createIdMemo] = useState(() => createRequestIdMemo());
  function nextBatchName() {
    return nextLayerBatchName(layerBatches || []);
  }

  // Pick up notice handed via navigation state (e.g. delete report from
  // record page if blocked detaches need surfacing on the hub).
  useEffect(() => {
    // `location.state` is `unknown` by design — src/app/navigation/appLocation.ts
    // states that no consumer may read it without proving its shape. This is that
    // proof, checking exactly the one key the record pages hand back here. A
    // truthy non-object state produced `undefined` for `.notice` before and
    // produces `null` here, so the branch is taken on the same inputs.
    const routedNotice =
      location.state && typeof location.state === 'object' && 'notice' in location.state
        ? (location.state.notice as Notice | null)
        : null;
    if (routedNotice) {
      setNotice(routedNotice);
      navigate(location.pathname, {replace: true, state: null});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timeline deep-link → /layer/batches/<id>
  useEffect(() => {
    // `pendingEdit` is `unknown` by design — UIContext stores the object and
    // hands it back without ever inspecting it. This proves the two fields this
    // branch reads, which is exactly the {id, viewName} contract UIContext's own
    // header documents.
    const pending =
      pendingEdit && typeof pendingEdit === 'object' ? (pendingEdit as {id?: string; viewName?: string}) : null;
    if (pending?.viewName === 'layerbatches' && pending?.id) {
      const id = pending.id;
      setPendingEdit && setPendingEdit(null);
      navigate('/layer/batches/' + id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdit]);

  const batchStats = React.useMemo(
    () => computeBatchStats(layerBatches || [], layerHousings || [], rawLayerDailys, rawEggDailys),
    [layerBatches, layerHousings, rawLayerDailys, rawEggDailys],
  );

  function openAdd() {
    // The new-batch name is derived from the route-owned directory.  An
    // independently mounted hub starts with no cached rows, so do not let an
    // editor calculate a plausible-but-duplicate name before that directory
    // has either resolved or reported its error.
    if (!canEdit || loading || loadError) return;
    setAddErr('');
    setAddName(nextBatchName());
    setAddArrivalDate('');
    setAddStatus('active');
    setShowAddBatch(true);
  }

  async function saveNewBatch() {
    if (!canEdit || loading || loadError) return;
    setAddErr('');
    const name = (addName || '').trim();
    if (!name) {
      setAddErr('Batch name required.');
      return;
    }
    // A renamed batch keeps its original id (L-26-01 -> LAY-26-01 is still
    // l-26-01), so refuse any name whose id is taken: the save is an upsert
    // and would otherwise overwrite that batch.
    if (layerBatchIdTaken(name, layerBatches || [])) {
      setAddErr('That batch name is already in use.');
      return;
    }
    setAddBusy(true);
    const id = layerBatchIdFromName(name);
    const rec = {
      id,
      name,
      status: addStatus || 'active',
      arrival_date: addArrivalDate || null,
      original_count: null,
      supplier: null,
      cost_per_bird: null,
      brooder_name: null,
      brooder_entry_date: null,
      brooder_exit_date: null,
      schooner_name: null,
      schooner_entry_date: null,
      schooner_exit_date: null,
      notes: null,
    };
    // 242: an insert-only batch.create. The id is the name's slug; an existing
    // id, or a renamed batch whose name still holds that slug, is refused by the
    // server instead of being overwritten. The server logs record.created.
    const createRequest = {
      operation: 'batch.create' as const,
      target: {batch_id: id},
      change: {name: rec.name, status: rec.status, arrival_date: rec.arrival_date},
    };
    const outcome = await applyLayerOperation(sb, {...createRequest, requestId: createIdMemo.idFor(createRequest)});
    if (outcome.status !== 'unknown') createIdMemo.settle();
    setAddBusy(false);
    if (outcome.status !== 'saved') {
      setAddErr(
        outcome.status === 'refused'
          ? 'That batch name is already in use.'
          : outcome.status === 'rejected'
            ? 'Could not save: ' + layerRejectionMessage(outcome)
            : outcome.status === 'unknown'
              ? outcome.message
              : 'Could not save. Try again.',
      );
      return;
    }
    // The exact hub owns no App state. Its destination is a still-legacy
    // record route, whose retained App reader performs the authoritative
    // hydration. Updating a stale global array here would make the released
    // route depend on the legacy boot contract it intentionally bypasses.
    setShowAddBatch(false);
    navigate('/layer/batches/' + id);
  }

  const totalBatches = (layerBatches || []).length;
  const activeBatches = (layerBatches || []).filter(
    (b) => (b.name === 'Retirement Home' ? retirementHomeStatus : b.status) === 'active',
  );

  // Default status ordering drives both grid render and record-sequence nav.
  const sorted = [...(layerBatches || [])].sort(buildLayerBatchComparator({key: 'status', dir: 'asc'}));
  const batchSeqRows = sorted;

  function decorateBatch(batch: LayerBatchRecord) {
    // `Partial` because the `|| {}` fallback is what a batch with no computed
    // stats lands on; every read below already carries its own `|| 0` or `> 0`.
    const stats: Partial<LayerStatTotals> = batchStats[batch.id] || {};
    const activeHousings = (layerHousings || []).filter((h) => h.batch_id === batch.id && h.status === 'active');
    const currentHens = activeHousings.reduce(
      (sum, housing) => sum + computeHousingDisplayCount(housing, rawLayerDailys, layerHousings),
      0,
    );
    return {
      ...batch,
      active_housing_names: activeHousings
        .map((h) => h.housing_name)
        .filter(Boolean)
        .join(', '),
      // The same names as a list, so each can carry its group dot (R06).
      active_housing_list: activeHousings.map((h) => h.housing_name).filter(Boolean) as string[],
      current_hens: currentHens,
      total_feed_lbs: stats.totalFeed || 0,
      total_mortality: stats.totalMort || 0,
      // Number() because a relational comparison and a division already coerced
      // through ToNumber; Number(undefined) is NaN, so a batch with no stats
      // still fails the test and still reaches the 0 branch.
      total_dozens: Number(stats.totalEggs) > 0 ? Math.floor(Number(stats.totalEggs) / 12) : 0,
      feed_cost: computeLayerFeedCost(stats.starterFeed, stats.growerFeed, stats.layerFeed, batch),
    };
  }
  const decoratedBatchRows = sorted.map(decorateBatch);

  return (
    <div style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
      <Header />
      <div
        style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}
        data-layer-batches-hub
        data-layer-batches-loaded={loading || loadError ? 'false' : 'true'}
      >
        <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
        <InlineNotice notice={loadError} />
        {loadError && (
          <Button type="button" onClick={loadLayerMetrics} style={{marginBottom: 12}}>
            Retry
          </Button>
        )}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <div>
            <h1 className="poultry-page-title">Layer Batches</h1>
            {/* Review F19: the count is a gray summary line under the H1, out
                of the heading, so the accessible name is exactly 'Layer
                Batches'. data-layer-batches-count is unchanged for the guard. */}
            <div
              style={{fontSize: 13, fontWeight: 400, color: 'var(--ink-muted)', marginTop: 2}}
              data-layer-batches-count
            >
              {sorted.length} of {totalBatches}
              {totalBatches > 0 ? ' · ' + activeBatches.length + ' active' : ''}
            </div>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            {canEdit && (
              <Button
                variant="primary"
                type="button"
                onClick={openAdd}
                disabled={loading || !!loadError}
                data-layer-new-batch
              >
                + New Batch
              </Button>
            )}
          </div>
        </div>

        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading...</div>}

        {!loading && !loadError && totalBatches === 0 && (
          <div
            data-empty-state="true-empty"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              padding: '2rem',
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            No layer batches yet. Click <strong>+ New Batch</strong> to plan one.
          </div>
        )}

        {/* true-empty (totalBatches === 0) is handled by the richer block above
            with the +New Batch call to action; the shared component renders the
            filtered-no-results message only. */}
        {totalBatches > 0 && (
          <OperationalListEmptyState
            loading={loading}
            loadError={loadError}
            totalCount={totalBatches}
            filteredCount={sorted.length}
            emptyLabel="No layer batches yet"
            filteredLabel="No layer batches match the current filters"
          />
        )}

        {!loading && !loadError && sorted.length > 0 && (
          <div
            className="poultry-layer-grid layer-batches-grid"
            // R08: one card, radius 18 on --shadow-card.
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              overflowX: 'auto',
            }}
          >
            {/* Header row — same grid template as every batch row below. */}
            <div
              data-layer-batches-grid-header
              className="poultry-layer-grid-row"
              style={{
                display: 'grid',
                gridTemplateColumns: LAYER_BATCH_GRID_COLUMNS,
                gap: 10,
                alignItems: 'center',
                padding: '8px 14px',
                // R02 / R29: heads written once, 11.5px uppercase in --label,
                // over a hairline - the same head row as the cattle lists.
                borderBottom: '1px solid var(--hairline)',
                background: 'var(--surface)',
                fontSize: 11.5,
                fontWeight: 700,
                color: 'var(--label)',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
              }}
            >
              <span>Batch</span>
              <span>Status</span>
              <span>Housings</span>
              <span>Hens</span>
              <span>Feed</span>
              <span>Mort.</span>
              <span>Dozens</span>
              <span>Cost</span>
              <span style={{textAlign: 'right'}}>Open</span>
            </div>
            {decoratedBatchRows.map((row) => {
              // `Partial` for the same reason as in decorateBatch: `|| {}` is
              // what a batch with no computed stats lands on.
              const stats: Partial<LayerStatTotals> = batchStats[row.id] || {};
              const isRetHome = row.name === 'Retirement Home';
              const isRetired = isRetHome ? retirementHomeStatus === 'inactive' : row.status === 'retired';
              const statusLabel = isRetHome
                ? retirementHomeStatus
                  ? layerGroupStatusLabel(retirementHomeStatus)
                  : groupResource.loading
                    ? 'Loading status…'
                    : 'Status unavailable'
                : isRetired
                  ? 'Retired'
                  : 'Active';
              const statusVariant = isRetHome
                ? retirementHomeStatus === 'active'
                  ? 'ok'
                  : 'neutral'
                : isRetired
                  ? 'neutral'
                  : 'ok';
              const fc = row.feed_cost;
              const retHomeDot = isRetHome ? layerGroupColor(groupPalette, row.name) : null;
              return (
                <div
                  key={row.id}
                  data-layer-batch-tile={row.id}
                  {...openableProps(() =>
                    navigate('/layer/batches/' + row.id, recordSeqNavOptions(labeledSeqItems(batchSeqRows, 'name'))),
                  )}
                  className="hoverable-tile poultry-layer-grid-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: LAYER_BATCH_GRID_COLUMNS,
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--hairline)',
                    cursor: 'pointer',
                    background: 'var(--surface)',
                  }}
                >
                  {/* Batch. Retirement Home is a group and carries its dot. */}
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: 'var(--ink)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {retHomeDot && (
                      <span style={{display: 'inline-flex', verticalAlign: 'middle', marginRight: 6}}>
                        <GroupDot color={retHomeDot} />
                      </span>
                    )}
                    {row.name}
                  </span>
                  {/* Status */}
                  <span style={{justifySelf: 'start'}}>
                    <Badge status={isRetHome ? retirementHomeStatus : row.status}>{statusLabel}</Badge>
                  </span>
                  {/* Active housings */}
                  <span
                    style={{
                      fontSize: 11,
                      color: row.active_housing_names ? 'var(--ink)' : 'var(--ink-faint)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isRetHome || !row.active_housing_names
                      ? '—'
                      : row.active_housing_list.map((name, i) => {
                          const dot = layerGroupColor(groupPalette, name);
                          return (
                            <React.Fragment key={name + ':' + i}>
                              {i > 0 ? ', ' : null}
                              {dot && (
                                <span style={{display: 'inline-flex', verticalAlign: 'middle', marginRight: 5}}>
                                  <GroupDot color={dot} />
                                </span>
                              )}
                              {name}
                            </React.Fragment>
                          );
                        })}
                  </span>
                  {/* Current hens */}
                  <span style={{fontSize: 12, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums'}}>
                    <span className="layer-batches-cell-label">Hens</span>
                    {row.current_hens > 0 ? <strong>{row.current_hens.toLocaleString()}</strong> : '—'}
                  </span>
                  {/* Feed */}
                  {/* Number() because the comparison and Math.round already
                      coerced through ToNumber; Number(undefined) is NaN, so a
                      batch with no stats still fails the test exactly as before. */}
                  <span
                    style={{
                      fontSize: 12,
                      color: Number(stats.totalFeed) > 0 ? 'var(--text-primary)' : 'var(--ink-faint)',
                    }}
                  >
                    <span className="layer-batches-cell-label">Feed</span>
                    {Number(stats.totalFeed) > 0 ? (
                      <strong>{Math.round(Number(stats.totalFeed)).toLocaleString() + ' lb'}</strong>
                    ) : (
                      '—'
                    )}
                  </span>
                  {/* Mortality — danger ink only when it flags a high loss */}
                  <span style={{fontSize: 12, fontVariantNumeric: 'tabular-nums'}}>
                    <span className="layer-batches-cell-label">Mort.</span>
                    {Number(stats.totalMort) > 10 ? (
                      <StatusText tone="danger">{row.total_mortality > 0 ? row.total_mortality : '0'}</StatusText>
                    ) : (
                      <span style={{color: 'var(--text-primary)'}}>
                        {row.total_mortality > 0 ? row.total_mortality : '0'}
                      </span>
                    )}
                  </span>
                  {/* Dozens */}
                  <span
                    style={{fontSize: 12, color: row.total_dozens > 0 ? 'var(--text-primary)' : 'var(--ink-faint)'}}
                  >
                    <span className="layer-batches-cell-label">Dozens</span>
                    {row.total_dozens > 0 ? row.total_dozens.toLocaleString() : '—'}
                  </span>
                  {/* Cost */}
                  <span style={{fontSize: 12, color: fc != null ? 'var(--text-primary)' : 'var(--ink-faint)'}}>
                    <span className="layer-batches-cell-label">Cost</span>
                    {fc != null
                      ? '$' + fc.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})
                      : '—'}
                  </span>
                  {/* Open */}
                  <span style={{fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right'}}>
                    <span className="chev" aria-hidden="true">
                      ›
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* + New Batch helper */}
      {showAddBatch && (
        <Modal
          title={'New Layer Batch'}
          onClose={() => {
            if (!addBusy) setShowAddBatch(false);
          }}
          size="sm"
          dialogAttrs={{'data-layer-new-batch-modal': '1'}}
          footer={
            <ModalFooter
              primaryLabel="Create & Open"
              onPrimary={saveNewBatch}
              busy={addBusy}
              cancelDisabled={addBusy}
              primaryAttrs={{'data-layer-new-batch-save': '1'}}
            />
          }
        >
          <div style={{display: 'grid', gap: 10}}>
            <div>
              <label
                style={{fontSize: 11, color: 'var(--ink-muted)', display: 'block', marginBottom: 3, fontWeight: 500}}
              >
                Batch Name *
              </label>
              <input value={addName} onChange={(e) => setAddName(e.target.value)} data-layer-new-batch-name />
            </div>

            <div>
              <label
                style={{fontSize: 11, color: 'var(--ink-muted)', display: 'block', marginBottom: 3, fontWeight: 500}}
              >
                Status
              </label>
              <select value={addStatus} onChange={(e) => setAddStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="retired">Retired</option>
              </select>
            </div>

            <div>
              <label
                style={{fontSize: 11, color: 'var(--ink-muted)', display: 'block', marginBottom: 3, fontWeight: 500}}
              >
                Arrival Date
              </label>
              <input type="date" value={addArrivalDate} onChange={(e) => setAddArrivalDate(e.target.value)} />
            </div>

            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-muted)',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '8px 10px',
              }}
            >
              Saves a batch shell and opens its record page. Fill in brooder, schooner, and costs there.
            </div>

            {addErr && <div style={{color: 'var(--danger-ink)', fontSize: 12, fontWeight: 600}}>{addErr}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
};

function LayerBatchesRouter() {
  const {authState} = useAuth();
  const location = useLocation();
  if (location.pathname.startsWith('/layer/housings/')) {
    // No `setLayerHousings`. The page's database write is page-local, and App's
    // post-write fan-out retired with the `layer.housings.detail` boot release
    // — see that surface's declaration for why the two had to land together.
    return React.createElement(LayerHousingPage, {sb, fmt, authState});
  }
  if (location.pathname.startsWith('/layer/batches/')) {
    // No setters either. Both of this page's App fan-outs retired with the
    // `layer.batches.detail` boot release; its three database writes were
    // always page-local and did not move.
    return React.createElement(LayerBatchPage, {sb, fmt, authState});
  }
  return React.createElement(LayerBatchesHub);
}

// The boot-released mounts for `/layer/housings/<id>` and `/layer/batches/<id>`,
// and the ONLY things in this module the released renderer touches.
//
// They are separate components rather than a reused LayerBatchesRouter for a
// reason that is structural, not stylistic. Neither page can be mounted from
// `renderReleasedAuthenticatedSurface` directly — that is a plain function, not
// a component, so there is no `useAuth` to call for the `authState` prop — and
// the router cannot be mounted in their place either, because it is exactly the
// thing that must keep answering for the shapes the two release fences REFUSE.
// Handing the renderer the router would mean a fenced shape and an audited one
// rendered through the same entry point, which is the distinction the whole
// arrangement exists to preserve.
//
// So the auth wiring lives here, beside the router branches these must stay
// identical to. Each prop literal is asserted equal to its branch's, character
// for character, in the two release proofs
// (tests/static/layer_housings_detail_boot_release_static.test.js and
// tests/static/layer_batches_detail_boot_release_static.test.js): a divergence
// between the released and legacy mounts of one page is exactly the drift those
// guards exist to catch.
export const LayerHousingRecordMount = () => {
  const {authState} = useAuth();
  return React.createElement(LayerHousingPage, {sb, fmt, authState});
};

export const LayerBatchRecordMount = () => {
  const {authState} = useAuth();
  return React.createElement(LayerBatchPage, {sb, fmt, authState});
};

export default LayerBatchesRouter;
